import { Component, OnDestroy, ViewChild } from '@angular/core';
import { DatePipe, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MEMBERS_PROFILE_APPROVE,
  MEMBERS_AGENDA,
  MEMBERS_DETAIL,
  MEMBERS_DETAIL_LOG,
} from 'manager/http-constants';
import { LoadingState } from 'src/app/shared/html-parts/loading/loading-state';
import {
  MessageData,
  ToastMessageData,
} from 'src/app/shared/html-parts/message-common/message-data';
import { CommonService } from 'src/app/shared/service/common.service';
import { DbOperationService } from 'src/app/shared/service/db-operation.service';
import { PlatformComponent } from '../platform.component';
import { TOAST } from 'src/app/shared/constant/primeng-constants';
import { MESSAGE_CODE } from 'src/app/shared/constant/message-constant';
import { DATE_FORMAT } from 'src/app/shared/constant/constant';
import { FormBuilder } from '@angular/forms';
import { Validator } from 'src/app/shared/validator/validator';
import { ApprovalDialogComponent } from '../common/approval-dialog/approval-dialog.component';
import { DiffTableComponent } from '../common/diff-table/diff-table.component';
import { FollowDialogComponent } from 'src/app/pages/platform/common/follow-dialog/follow-dialog.component';
import { LoginService } from 'src/app/shared/service/login.service';
import { SESSION_KEY } from 'src/app/shared/constant/session-constants';

@Component({
  selector: 'app-b212',
  templateUrl: './b212.component.html',
  styleUrls: ['./b212.component.scss', '../platform.component.scss'],
})

/**
 * 会員プロフィール(民間団体)
 */
export class B212Component extends PlatformComponent implements OnDestroy {
  /** 共通部品_承認時ダイアログ */
  @ViewChild(ApprovalDialogComponent)
  approvalDialogComponent: ApprovalDialogComponent;

  /** 共通部品_差分比較 */
  @ViewChild(DiffTableComponent)
  diffTableComponent: DiffTableComponent;

  @ViewChild(FollowDialogComponent)
  followDialogComponent: FollowDialogComponent;

  // 会員ID
  memberId: string = this.route.snapshot.params.member_id;

  // 会員情報格納先
  memberInformation: any;

  // 課題リスト格納先
  taskList: any[];
  defaultShouldReuseRouteStrat: any;

  constructor(
    public route: ActivatedRoute,
    public commonService: CommonService,
    public dbOperationService: DbOperationService,
    public loadingState: LoadingState,
    private messageData: MessageData,
    private location: Location,
    private datePipe: DatePipe,
    private router: Router,
    private loginService: LoginService
  ) {
    super(route, commonService, dbOperationService, loadingState);
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.defaultShouldReuseRouteStrat;
  }

  async ngOnInit(): Promise<void> {
    this.defaultShouldReuseRouteStrat = this.router.routeReuseStrategy.shouldReuseRoute;
    this.router.routeReuseStrategy.shouldReuseRoute = () => { return false; };
    await this.getUserLogin();
    if(!this.loginUser){
      this.loginService.logout();
      return;
    }

    // 辞書値リストを取得
    this.getSpecCodeList(501);

    // 会員情報を取得
    this.getMemberInformation();

    // 課題情報を取得
    this.getTaskInformation();
  }

  /**
   * 会員情報を取得
   */
  public getMemberInformation(): void {
    // 画面ロードフラグをON(ロード中状態)
    this.loadingState.loadStart(MEMBERS_DETAIL);

    // 会員詳細を取得
    this.dbOperationService
      .getNoTemplateDisplayData(MEMBERS_DETAIL, this.memberId, true)
      .subscribe((response) => {
        // 画面ロードフラグをOFF(ロード終了)
        this.loadingState.loadSleepEnd(0.3, MEMBERS_DETAIL);

        if (this.commonService.checkNoneResponse(response)) {
          // 警告メッセージ
          this.messageData.toastMessage(
            new ToastMessageData({
              severity: TOAST.WARN,
              summary: this.commonService.msg(MESSAGE_CODE.E00003),
              detail: this.commonService.msg(MESSAGE_CODE.E00026),
            })
          );

          // 前画面へ戻る
          this.location.back();
        } else {
          if(response.body.status.id == 3 
            && !this.decisionSecretariat() 
            && !this.decisionPerspective(response.body.member_id)
          ){
            this.messageData.toastMessage(
              new ToastMessageData({
                severity: TOAST.ERROR,
                summary: this.commonService.msg(MESSAGE_CODE.E00003),
                detail: this.commonService.msg(MESSAGE_CODE.E80002),
                position: TOAST.BOTTOM_RIGHT,
                life: 60000,
              })
            );
            this.loginService.logout(MESSAGE_CODE.N90001);
            return;
          }
          // 会員詳細を格納
          this.memberInformation = response.body;
        }
      });
  }

  /**
   * 課題情報を取得
   */
  public getTaskInformation(): void {
    // 画面ロードフラグをON(ロード中状態)
    this.loadingState.loadStart(MEMBERS_AGENDA);

    let requestBody = {
      member_id_login: this.loginUser ? this.loginUser.member_id : "0"
    }

    // 課題情報を取得
    this.dbOperationService
      .gettNoTemplateData(MEMBERS_AGENDA + this.memberId, requestBody, true)
      .subscribe((response) => {
        if (!this.commonService.checkNoneResponse(response)) {
          // 課題情報を格納
          this.taskList = response.body;
        } else {
          this.taskList = new Array();
        }

        // 画面ロードフラグをOFF(ロード終了)
        this.loadingState.loadSleepEnd(0.3, MEMBERS_AGENDA);
      });
  }

  /**
   * 会員登録・更新画面(入力)(B101)へ遷移する
   */
  public moveB101(): void {
    // 会員登録・更新画面(入力)(B101)へ遷移
    this.router.navigate(['pages/platform/b101/edit'], {
      queryParams: {
        screenId: 'b101',
        member_id: this.memberId,
        class_id: this.memberInformation.class_id.id,
      },
    });
  }

  /**
   *  課題・ソリューション登録(C101)へ遷移する
   * @param agendaKbn 区分 1:課題　2:ソリューション
   */
  public moveC101(agendaKbn: number): void {
    // 課題・ソリューション登録(C101)へ遷移
    this.router.navigate(['pages/platform/c101/new'], {
      queryParams: {
        screenId: 'c101',
        agenda_kbn: agendaKbn,
      },
    });
  }

  /**
   * 課題・ソリューション詳細(C301)または連携事例詳細(H301)へ遷移する
   * @param agendaId 課題ID
   */
  public moveC301orMoveH301(agendaKbn: string, agendaId: string): void {

    //課題・ソリューション or 連携事例か
    if ('1' == agendaKbn || '2' == agendaKbn) {
      // 課題・ソリューション詳細(C301)へ遷移
      this.router.navigate(['pages/platform/c301/' + agendaId]);
    } else {
      // 連携事例詳細(H301)へ遷移
      this.router.navigate(['pages/platform/h301/' + agendaId]);
    }
  }


  /**
   * 公開判定フラグ
   * @returns 0:表示なし 1:公開中 2:非公開
   */
  public releaseFlag(status: number, endDate: string, agenda_kbn_id: number, past_issue:number): number {
    // 会員詳細の判定
    if (!this.memberInformation) {
      // 会員詳細が存在しない場合

      return 0;
    }

    // 課題記載ユーザ or 事務局ユーザー　でない
    if (!(this.decisionPerspective(this.memberInformation.member_id) || this.decisionSecretariat())) {
      // 承認済み以外の場合
      if (2 != status) {
        return 0;
      }
    }

    // 課題記載ユーザ or 事務局ユーザーか否か
    if (
      this.decisionPerspective(this.memberInformation.member_id) ||
      this.decisionSecretariat()
    ) {
      // 課題記載ユーザ or 事務局ユーザーの場合

      if(agenda_kbn_id == 1 && past_issue == 1){
        if (status == 2) {
          return 1;
        } else {
          return 2;
        }
      }else{
        // 掲載終了日が存在しない場合
        if (!endDate && status == 2) {
          return 1;
        }

        // 掲載終了日の判定
        if (
          this.datePipe.transform(new Date(), DATE_FORMAT.DATE) <=
          this.datePipe.transform(new Date(endDate), DATE_FORMAT.DATE)
        ) {
          // 掲載終了日より前の場合
          if (status == 2) {
            return 1;
          } else {
            return 2;
          }

        } else {
          // 掲載終了日より後の場合

          return 2;
        }
      }

    } else if (!this.decisionPerspective(this.memberInformation.member_id)) {
      // 未ログイン or 他企業の場合
      if(agenda_kbn_id == 1 && past_issue == 1){
        if (status == 2) {
          return 1;
        } else {
          return 2;
        }
      }else{
        // 掲載終了日が存在しない場合
        if (!endDate && status == 2) {
          return 1;
        }
        // 掲載終了日の判定
        if (
          this.datePipe.transform(new Date(), DATE_FORMAT.DATE) <=
          this.datePipe.transform(new Date(endDate), DATE_FORMAT.DATE)
        ) {
          // 掲載終了日より前の場合
          if (status == 2) {
            return 1;
          } else {
            return 2;
          }
        }
      }
    }
    return 0;
  }



  // /**
  //  * 公開判定フラグ
  //  * @returns 0:表示なし 1:公開中 2:非公開
  //  */
  //  public releaseFlag(status: number, endDate: string): number {
  //   // 会員詳細の判定
  //   if (!this.memberInformation) {
  //     // 会員詳細が存在しない場合

  //     return 0;
  //   }

  //   // 課題記載ユーザ or 事務局ユーザー　でない
  //   if (!(this.decisionPerspective(this.memberInformation.member_id) || this.decisionSecretariat())){
  //       // 承認済み以外の場合
  //       if (2 != status) {
  //         return 0;
  //       }
  //   }

  //   // 課題記載ユーザ or 事務局ユーザーか否か
  //   if (
  //     this.decisionPerspective(this.memberInformation.member_id) ||
  //     this.decisionSecretariat()
  //   ) {
  //     // 課題記載ユーザ or 事務局ユーザーの場合

  //     // 掲載終了日が存在しない場合
  //     if (!endDate) {
  //       return 1;
  //     }

  //     // 掲載終了日の判定
  //     if (
  //       this.datePipe.transform(new Date(), DATE_FORMAT.DATE) <=
  //       this.datePipe.transform(new Date(endDate), DATE_FORMAT.DATE)
  //     ) {
  //       // 掲載終了日より前の場合

  //       return 1;
  //     } else {
  //       // 掲載終了日より後の場合

  //       return 2;
  //     }
  //   } else if(!this.decisionPerspective(this.memberInformation.member_id)){
  //     // 未ログイン or 他企業の場合

  //     // 掲載終了日が存在しない場合
  //     if (!endDate) {
  //       return 1;
  //     }
  //     // 掲載終了日の判定
  //     if (
  //       this.datePipe.transform(new Date(), DATE_FORMAT.DATE) <=
  //       this.datePipe.transform(new Date(endDate), DATE_FORMAT.DATE)
  //     ) {
  //       // 掲載終了日より前の場合

  //       return 1;
  //     }
  //   }

  //   return 0;
  // }


  // /**
  //  * 公開判定フラグ
  //  * @param status ステータス
  //  * @param endDate 掲載終了日
  //  * @returns 0:課題非表示 1:公開中 2:非公開
  //  */
  // public releaseFlag(status: number, endDate: string): number {
  //   // 会員詳細の判定
  //   if (!this.memberInformation) {
  //     // 会員詳細が存在しない場合

  //     return 0;
  //   }

  //   // 課題記載ユーザ or 事務局ユーザーか否か
  //   if (
  //     !(
  //       this.decisionPerspective(this.memberInformation.member_id) ||
  //       this.decisionSecretariat()
  //     )
  //   ) {
  //     // 課題記載ユーザ or 事務局ユーザー以外の場合

  //     // 承認済み以外の場合
  //     if (2 != status) {
  //       return 0;
  //     }
  //   }

  //   // 掲載終了日が存在しない場合
  //   if (!endDate) {
  //     return 1;
  //   }

  //   // 掲載終了日の判定
  //   if (
  //     this.datePipe.transform(new Date(), DATE_FORMAT.DATE) <=
  //     this.datePipe.transform(new Date(endDate), DATE_FORMAT.DATE)
  //   ) {
  //     // 掲載終了日より前の場合

  //     return 1;
  //   } else {
  //     // 掲載終了日より後の場合

  //     // 課題記載ユーザ or 事務局ユーザーか否か
  //     if (
  //       this.decisionPerspective(this.memberInformation.member_id) ||
  //       this.decisionSecretariat()
  //     ) {
  //       // 課題記載ユーザ or 事務局ユーザーの場合
  //       return 2;
  //     } else {
  //       // 上記以外
  //       return 0;
  //     }
  //   }
  // }

  /**
   * 課題表示判定フラグ
   * @returns true:表示 false:非表示
   */
  public taskDisplayJudgment(): boolean {
    // 課題リストが存在するか否か
    if (!this.taskList?.length) {
      // 課題リストが存在しない場合

      return false;
    }

    // 課題リストから課題非表示以外を取得
    let taskList: any[] = this.taskList.map((task) => {
      if (0 != this.releaseFlag(task.status.id, task.end_date, task.agenda_kbn.id,task.created_by_follow)) {
        return task;
      }
    });

    // 配列から空要素削除
    taskList = taskList.filter((v) => v);

    // 課題非表示を抜いた課題リストが存在しているか否か
    if (taskList.length) {
      // 課題リストが存在している場合
      return true;
    }

    return false;
  }

  /**
   * 内閣府承認ダイアログ表示
   */
  public openSmallApprovalDialog(): void {
    // 内閣府承認ダイアログ表示
    this.approvalDialogComponent.openSmallApprovalDialog(
      MEMBERS_PROFILE_APPROVE,
      this.memberId,
      2002,
      0,
      1
    );
  }

  /**
   * 差分ダイアログ表示
   */
  public openDiffDialog(screen_id: string): void {
    // 差分比較ダイアログ表示
    this.diffTableComponent.openDiffTable(
      screen_id,
      MEMBERS_DETAIL,
      MEMBERS_DETAIL_LOG,
      this.memberId,
      2002,
      0,
      1
    );
  }


  /**
   * 会員一覧(A110)へ遷移する
   *
   */
  public moveA110(): void {
    //会員一覧(A110)へ遷移する
    this.router.navigate(['pages/platform/a110']);
  }

  /**
   * open dialog follow
   * @param agendaId agenda to follow
   * @param member_id created member id
   * @param agendaTitle title to display
   * @returns
   */
  openConfirmFollowdialog(agendaId:string,member_id:string,agendaTitle:string){
    if(!this.loginUser||this.loginUser.class_id!=9||this.loginUser.class_id!=3){
      this.followDialogComponent.showConfirmFollowDialog(agendaId,agendaTitle,member_id);
    }
  }

  /**
   * open dialog unfollow
   * @param agendaId agenda to unfollow
   * @param agendaTitle agenda title to display
   * @returns
   */
  openConfirmUnFollowdialog(agendaId:string,agendaTitle:string){
    this.followDialogComponent.showConfirmUnFollowDialog(agendaId,agendaTitle);
  }

  async getUserLogin(){
    return new Promise(resolve => {
      this.loginService.getAuth0LoginState().subscribe((response) => {
        // ログイン状態をセット

        // ログイン状態を判定
        if (response) {
          // ログイン状態の場合

          /* ログインユーザー情報 */
          // セッションからログインユーザ情報取得
          const loginUser = JSON.parse(
            window.sessionStorage.getItem(SESSION_KEY.loginUserInformation)
          );

          // セッションにログインユーザ情報が存在するか否か
          if (loginUser) {
            // セッションにログインユーザが存在する場合

            // 課題・ソリューション結果を取得
            return resolve(null);
          } else {
            // セッションにログインユーザが存在しない場合(初回表示時)

            // ログインユーザ情報取得処理
            this.loginService.getLoginUser().subscribe((response) => {
              // 正常なユーザの為、セッションにユーザ情報を格納
              window.sessionStorage.setItem(
                SESSION_KEY.loginUserInformation,
                JSON.stringify(response.body[0])
              );

              this.loginUser = JSON.parse(window.sessionStorage.getItem(SESSION_KEY.loginUserInformation));
              // 課題・ソリューション結果を取得
              return resolve(null);
            });
          }
        } else {
          // 未ログイン状態の場合

          return resolve(null);
        }
      });
    });
  }
}
